import type {
  AtlasEdge, AtlasNode, AtlasWorkspace, Confidence, ImportRow, NodeType, RelationshipType,
} from './types';

const NODE_TYPES = new Set<NodeType>([
  'capability', 'application', 'service', 'data_store', 'identity',
  'infrastructure', 'vendor', 'procedure', 'role', 'control',
]);
const RELATIONSHIP_TYPES = new Set<RelationshipType>([
  'DEPENDS_ON', 'AUTHENTICATES_VIA', 'TRUSTS_IDENTITY_FROM', 'STORES_DATA_IN',
  'HOSTED_ON', 'PROVIDED_BY', 'FALLS_BACK_TO', 'REQUIRES_ROLE', 'HAS_ACCESS_TO',
  'SENDS_DATA_TO', 'CONNECTS_TO', 'MONITORED_BY',
]);
const CONFIDENCE = new Set<Confidence>(['confirmed', 'reported', 'inferred', 'unknown']);
const STRENGTHS = new Set<AtlasEdge['strength']>(['hard', 'degraded', 'optional']);
const NODE_TYPE_ALIASES: Record<string, NodeType> = {
  business_capability: 'capability', app: 'application', database: 'data_store', datastore: 'data_store',
  identity_provider: 'identity', idp: 'identity', infra: 'infrastructure', team: 'role', process: 'procedure',
};

export interface DependencyImportPlan {
  filename: string;
  validRows: number;
  warnings: string[];
  newNodeCount: number;
  newEdgeCount: number;
  duplicateCount: number;
  candidate: AtlasWorkspace;
}

function normalizedToken(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replaceAll(' ', '_').replaceAll('-', '_');
}

export function normalizeNodeType(value: unknown): NodeType {
  const normalized = normalizedToken(value);
  const resolved = NODE_TYPE_ALIASES[normalized] ?? normalized;
  return NODE_TYPES.has(resolved as NodeType) ? resolved as NodeType : 'application';
}

export function normalizeRelationship(value: unknown): RelationshipType {
  const normalized = String(value ?? 'DEPENDS_ON').trim().toUpperCase().replaceAll(' ', '_').replaceAll('-', '_');
  return RELATIONSHIP_TYPES.has(normalized as RelationshipType) ? normalized as RelationshipType : 'DEPENDS_ON';
}

function normalizeRows(rows: Record<string, unknown>[]): { rows: ImportRow[]; warnings: string[] } {
  const normalized: ImportRow[] = [];
  const warnings: string[] = [];
  rows.forEach((raw, index) => {
    const rowNumber = index + 2;
    const lower = Object.fromEntries(Object.entries(raw).map(([key, value]) => [key.toLowerCase().replaceAll(/[^a-z0-9]/g, ''), value]));
    const source = String(lower.source ?? lower.sourcename ?? lower.from ?? '').trim();
    const target = String(lower.target ?? lower.targetname ?? lower.to ?? '').trim();
    if (!source || !target) {
      warnings.push(`Row ${rowNumber}: missing source or target`);
      return;
    }
    const sourceTypeValue = lower.sourcetype ?? lower.fromtype;
    const targetTypeValue = lower.targettype ?? lower.totype;
    const relationshipValue = lower.relationship ?? lower.relation ?? lower.edgetype;
    const sourceTypeToken = normalizedToken(sourceTypeValue);
    const targetTypeToken = normalizedToken(targetTypeValue);
    const relationshipToken = String(relationshipValue ?? '').trim().toUpperCase().replaceAll(' ', '_').replaceAll('-', '_');
    if (sourceTypeToken && !NODE_TYPE_ALIASES[sourceTypeToken] && !NODE_TYPES.has(sourceTypeToken as NodeType)) warnings.push(`Row ${rowNumber}: unknown source type “${String(sourceTypeValue)}” defaulted to application`);
    if (targetTypeToken && !NODE_TYPE_ALIASES[targetTypeToken] && !NODE_TYPES.has(targetTypeToken as NodeType)) warnings.push(`Row ${rowNumber}: unknown target type “${String(targetTypeValue)}” defaulted to application`);
    if (relationshipToken && !RELATIONSHIP_TYPES.has(relationshipToken as RelationshipType)) warnings.push(`Row ${rowNumber}: unknown relationship “${String(relationshipValue)}” defaulted to DEPENDS ON`);

    const criticalityValue = lower.criticality === undefined || lower.criticality === '' ? undefined : Number(lower.criticality);
    if (criticalityValue !== undefined && (!Number.isFinite(criticalityValue) || criticalityValue < 1 || criticalityValue > 5)) warnings.push(`Row ${rowNumber}: criticality must be 1–5 and was defaulted to 3`);
    const strengthToken = normalizedToken(lower.strength);
    if (strengthToken && !STRENGTHS.has(strengthToken as AtlasEdge['strength'])) warnings.push(`Row ${rowNumber}: unknown strength “${String(lower.strength)}” defaulted to hard`);
    const confidenceToken = normalizedToken(lower.confidence);
    if (confidenceToken && !CONFIDENCE.has(confidenceToken as Confidence)) warnings.push(`Row ${rowNumber}: unknown confidence “${String(lower.confidence)}” defaulted to reported`);
    const toleranceValue = lower.toleranceminutes ?? lower.tolerance;
    const parsedTolerance = toleranceValue === undefined || toleranceValue === '' ? 0 : Number(toleranceValue);
    if (!Number.isFinite(parsedTolerance) || parsedTolerance < 0) warnings.push(`Row ${rowNumber}: tolerance must be zero or greater and was defaulted to 0`);

    normalized.push({
      source,
      sourceType: normalizeNodeType(sourceTypeValue),
      relationship: normalizeRelationship(relationshipValue),
      target,
      targetType: normalizeNodeType(targetTypeValue),
      criticality: criticalityValue !== undefined && Number.isFinite(criticalityValue) && criticalityValue >= 1 && criticalityValue <= 5 ? criticalityValue : undefined,
      owner: String(lower.owner ?? ''),
      strength: STRENGTHS.has(strengthToken as AtlasEdge['strength']) ? strengthToken as AtlasEdge['strength'] : 'hard',
      toleranceMinutes: Number.isFinite(parsedTolerance) && parsedTolerance >= 0 ? parsedTolerance : 0,
      confidence: CONFIDENCE.has(confidenceToken as Confidence) ? confidenceToken as Confidence : 'reported',
    });
  });
  return { rows: normalized, warnings };
}

export function planDependencyImport(
  workspace: AtlasWorkspace,
  rawRows: Record<string, unknown>[],
  filename: string,
  createNodeId: () => string,
  createEdgeId: () => string,
): DependencyImportPlan {
  const { rows, warnings } = normalizeRows(rawRows);
  const nextNodes = workspace.nodes.map((node) => ({ ...node }));
  const nextEdges = workspace.edges.map((edge) => ({ ...edge }));
  let newNodeCount = 0;
  let newEdgeCount = 0;
  let duplicateCount = 0;

  const getNode = (name: string, type: NodeType, owner?: string, criticality?: number): AtlasNode => {
    const existing = nextNodes.find((node) => node.name.toLowerCase() === name.toLowerCase() && node.type === type);
    if (existing) return existing;
    const index = nextNodes.length;
    const node: AtlasNode = {
      id: createNodeId(), name, type, description: `Imported from ${filename}.`, owner: owner ?? '',
      criticality: Math.min(5, Math.max(1, criticality || 3)) as AtlasNode['criticality'],
      environment: 'production', confidence: 'reported', tags: ['Imported'], dataClassifications: [],
      sourceRefs: [filename], locationIds: [], x: 85 + (index % 4) * 230, y: 95 + Math.floor(index / 4) * 185,
    };
    nextNodes.push(node);
    newNodeCount += 1;
    return node;
  };

  for (const row of rows) {
    const source = getNode(row.source, row.sourceType, row.owner, row.criticality);
    const target = getNode(row.target, row.targetType);
    const duplicate = nextEdges.some((edge) => edge.sourceId === source.id && edge.targetId === target.id && edge.type === row.relationship);
    if (duplicate) {
      duplicateCount += 1;
      continue;
    }
    nextEdges.push({
      id: createEdgeId(), sourceId: source.id, targetId: target.id, type: row.relationship,
      strength: row.strength ?? 'hard', toleranceMinutes: row.toleranceMinutes ?? 0,
      confidence: row.confidence ?? 'reported',
      failureEffect: row.strength === 'degraded' ? 'degraded' : row.strength === 'optional' ? 'none' : 'unavailable',
      propagationDelayMinutes: 0, evidenceIds: [],
    });
    newEdgeCount += 1;
  }

  return {
    filename,
    validRows: rows.length,
    warnings: rows.length ? warnings : warnings.length ? warnings : ['No dependency rows were recognized.'],
    newNodeCount,
    newEdgeCount,
    duplicateCount,
    candidate: { ...workspace, nodes: nextNodes, edges: nextEdges },
  };
}
